<template>
  <div
    id="collapsed_sidebar"
    class="flex flex-col w-full h-full justify-between"
  >
    <div id="collapsed_sidebar_top_options" class="flex flex-col gap-1">
      <template v-for="option in options" :key="option.id">
        <router-link
          v-if="
            !option.sections &&
            !option.event &&
            option.alignment === sidebarOptionPosition.TOP
          "
          :to="option.route || ''"
          :class="{ 'pointer-events-none': !option.route }"
        >
          <a-tooltip placement="right" color="var(--tooltip-bg)">
            <template #title
              ><span class="text_color">{{ option.text }}</span></template
            >

            <SidebarItem
              :option="option"
              :is-expanded="false"
              :is-selected="isOptionSelected(option)"
              :is-disabled="!option.route"
            />
          </a-tooltip>
        </router-link>

        <div
          v-else-if="
            option.event && option.alignment === sidebarOptionPosition.TOP
          "
          @click="$emit('event-click', option.id)"
          class="cursor-pointer"
        >
          <a-tooltip placement="right" color="var(--tooltip-bg)">
            <template #title>{{ option.text }}</template>

            <SidebarItem
              :option="option"
              :is-expanded="false"
              :is-selected="false"
              :is-disabled="false"
            />
          </a-tooltip>
        </div>

        <router-link
          v-else-if="option.sections && isDirectLinkApp(option.id, option.sections?.some(s => s.externalUrl))"
          :to="option.route || option.sections?.[0]?.route || ''"
          :class="{
            'pointer-events-none': !(
              option.route || option.sections?.[0]?.route
            ),
          }"
        >
          <a-tooltip placement="right" color="var(--tooltip-bg)">
            <template #title>{{ option.text }}</template>

            <SidebarItem
              :option="option"
              :is-expanded="false"
              :is-selected="isOptionSelected(option)"
              :is-disabled="!(option.route || option.sections?.[0]?.route)"
            />
          </a-tooltip>
        </router-link>

        <CustomDropdown
          v-else-if="option.sections"
          :ref="(el) => setDropdownRef(option.id, el)"
          :key="option.id"
          maxHeight="500px"
          position="right"
          width="w-full"
          :on-hover="!isAppStoreV2"
          :hover-delay="800"
          :relative-to-viewport="true"
        >
          <template #trigger>
            <div>
              <SidebarItem
                  :option="option"
                  :is-expanded="false"
                  :is-selected="isOptionSelected(option)"
                  :show-arrow="true"
                  :hideIcon="false"
                  :show-indicator="option.id === 'projects' && (hasUnreadIndicator || totalUnreadCount > 0)"
                  :unread-count="option.id === 'projects' ? chatsUnreadDisplayCount : 0"
              />
            </div>
          </template>

          <template #default>
            <div class="flex flex-col min-w-44">
              <div class="flex items-center justify-between px-2 py-[6px]">
                <span class="text-base text_color font-semibold">
                  {{ option.text }}
                </span>
              </div>

              <div class="flex flex-col gap-1">
                <template v-for="section in option.sections" :key="section.id">
                  <!-- Parent section with sub_sections -->
                  <div v-if="section?.sub_sections?.length" class="relative">
                    <!-- CRM parent section with route - clickable and toggleable -->
                    <router-link
                      v-if="section.route && section.route.includes('/crm/')"
                      :to="section.route"
                      custom
                      v-slot="{ navigate, href }"
                    >
                      <a
                        :href="href"
                        @click="
                          (e) =>
                            handleParentSectionClick(
                              e,
                              navigate,
                              section.id,
                              scrollToSection,
                            )
                        "
                        class="dropdown-item group block w-full"
                        style="text-decoration: none; color: inherit"
                      >
                        <SidebarItem
                          :option="section"
                          :is-expanded="true"
                          :is-selected="isOptionSelected(section)"
                          :show-arrow="true"
                          :is-open="activeSubSection === section.id"
                          :hideIcon="true"
                          :app-id="section.appId"
                          :app-name="section.appName"
                          dropdown-placement="right"
                          :always-visible-dropdown="true"
                          @dropdown-open-change="
                            (open) => handleDropdownOpenChange(open, section.id)
                          "
                          @dropdown-mode-change="
                            (mode) => handleModeChange(mode, section.id)
                          "
                        />
                      </a>
                    </router-link>

                    <!-- Non-CRM parent section - only toggleable -->
                    <div
                      v-else
                      class="dropdown-item group"
                      @click="toggleSubSection(section.id, scrollToSection)"
                    >
                      <SidebarItem
                        :option="section"
                        :is-expanded="true"
                        :is-selected="isOptionSelected(section)"
                        :show-arrow="true"
                        :is-open="activeSubSection === section.id"
                        :hideIcon="true"
                        :app-id="section.appId"
                        :app-name="section.appName"
                        dropdown-placement="right"
                        :always-visible-dropdown="true"
                        @dropdown-open-change="
                          (open) => handleDropdownOpenChange(open, section.id)
                        "
                        @dropdown-mode-change="
                          (mode) => handleModeChange(mode, section.id)
                        "
                      />
                    </div>

                    <transition
                      @enter="startEnterTransition"
                      @leave="startLeaveTransition"
                      @after-enter="endTransition"
                      @after-leave="endTransition"
                    >
                      <div
                        v-show="activeSubSection === section.id"
                        class="ml-4 flex flex-col gap-1 mt-1 overflow-hidden"
                        :ref="(el) => setSubSectionRef(section.id, el)"
                      >
                        <SubSectionItem
                          v-for="subSection in section.sub_sections"
                          :key="subSection.id"
                          :item="subSection"
                          :depth="1"
                          :is-collapsed="true"
                          :option-id="option.id"
                          :parent-route="section.route"
                          dropdown-placement="right"
                          :always-visible-dropdown="true"
                          @dropdown-open-change="handleDropdownOpenChange"
                          @dropdown-mode-change="handleModeChange"
                          @item-click="handleItemClick"
                        />
                      </div>
                    </transition>
                  </div>

                  <router-link
                    v-else
                    :to="section.route || ''"
                    :class="[
                      'dropdown-item group',
                      { 'pointer-events-none': !section.route },
                    ]"
                    @click="() => handleItemClick(option.id)"
                  >
                    <SidebarItem
                      :option="section"
                      :is-expanded="true"
                      :is-selected="isOptionSelected(section)"
                      :is-disabled="!section.route"
                      :show-coming-soon="!section.route"
                      :hideIcon="true"
                      :app-id="section.appId"
                      :app-name="section.appName"
                      :show-indicator="isChatsSection(section) && (hasUnreadIndicator || totalUnreadCount > 0)"
                      :unread-count="isChatsSection(section) ? chatsUnreadDisplayCount : 0"
                      dropdown-placement="right"
                      :always-visible-dropdown="true"
                      @dropdown-open-change="
                        (open) => handleDropdownOpenChange(open, section.id)
                      "
                      @dropdown-mode-change="
                        (mode) => handleModeChange(mode, section.id)
                      "
                    />
                  </router-link>
                </template>
              </div>
            </div>
          </template>
        </CustomDropdown>
      </template>
    </div>

    <div id="collapsed_sidebar_bottom_options" class="flex flex-col gap-1">
      <template
        v-for="option in options"
        :key="`${option?.id}_collapsed_bottom_option`"
      >
        <div
          v-if="
            option.alignment === sidebarOptionPosition.BOTTOM && option.event
          "
          @click="$emit('event-click', option.id)"
          class="cursor-pointer"
        >
          <a-tooltip placement="right" color="var(--tooltip-bg)">
            <template #title>{{ option.text }}</template>

            <SidebarItem
              :option="option"
              :is-expanded="false"
              :is-selected="false"
            />
          </a-tooltip>
        </div>

        <div
          v-else-if="
            option.alignment === sidebarOptionPosition.BOTTOM && !option.event
          "
        >
          <a-tooltip placement="right" color="var(--tooltip-bg)">
            <template #title>{{ option.text }}</template>

            <SidebarItem
              :option="option"
              :is-expanded="false"
              :is-selected="isOptionSelected(option)"
              :is-disabled="!option.route"
            />
          </a-tooltip>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, provide } from "vue";
import { useStore } from "vuex";
import { useTransition } from "@/src/composables/useTransition";
import { useSidebarLogic } from "@/src/composables/useSidebarLogic";
import { isAppStoreV2 as isV2 } from "@/src/utils/appStoreUtils.js";
import { isDirectLinkApp } from "@/src/constants/appCodeConstants.js";
import { sidebarOptionPosition } from "@/src/constants/sidebarConstants.js";
import { isChatsSection } from "@/src/utils/sidebarUtils.js";
import "@/src/styles/transition.css";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import SidebarItem from "@/src/components/sidebar_components/SidebarItem.vue";
import SubSectionItem from "@/src/components/sidebar_components/SubSectionItem.vue";

const props = defineProps({
  options: { type: Array, required: true },
});

const emit = defineEmits(["event-click"]);

const store = useStore();
const isAppStoreV2 = computed(() => isV2(store));
const hasUnreadIndicator = computed(() => store.getters["chats/hasUnreadIndicator"]);
const totalUnreadCount = computed(() => store.getters["chats/totalUnreadCount"]);
const chatsUnreadDisplayCount = computed(() => {
  const count = totalUnreadCount.value || 0;
  if (count > 0) return count;
  return hasUnreadIndicator.value ? 1 : 0;
});
const dropdownRefs = ref({});
const subSectionRefs = ref({});
const currentOpenDropdown = ref(null);
const { startEnterTransition, startLeaveTransition, endTransition } =
  useTransition();

const {
  activeSubSection,
  toggleSubSection,
  toggleNestedSubSection,
  getNestedSubSectionState,
  handleDropdownOpenChange,
  handleModeChange,
  isOptionSelected,
} = useSidebarLogic();

// Provide shared functions to child components
provide("toggleNestedSubSection", toggleNestedSubSection);
provide("getNestedSubSectionState", getNestedSubSectionState);
provide("isOptionSelected", isOptionSelected);

const setDropdownRef = (id, el) => {
  if (el) {
    dropdownRefs.value[id] = el;
    watch(
      () => el.isOpen,
      (isOpen) => {
        if (isOpen) {
          if (currentOpenDropdown.value && currentOpenDropdown.value !== id) {
            dropdownRefs.value[currentOpenDropdown.value]?.closeDropdown();
          }
          currentOpenDropdown.value = id;
          activeSubSection.value = null;
        } else if (currentOpenDropdown.value === id) {
          currentOpenDropdown.value = null;
        }
      },
    );
  }
};

const setSubSectionRef = (id, el) => {
  if (el) {
    subSectionRefs.value[id] = el;
  }
};

const scrollToSection = (sectionId) => {
  if (subSectionRefs.value[sectionId]) {
    subSectionRefs.value[sectionId].scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }
};

const handleItemClick = (mainDropdownId) => {
  dropdownRefs.value[mainDropdownId]?.closeDropdown();
  currentOpenDropdown.value = null;
};

// Handle parent section clicks - both navigate and toggle for CRM routes
const handleParentSectionClick = (e, navigate, sectionId, scrollCallback) => {
  // Check if clicking on the arrow icon specifically
  const clickedElement = e.target;
  const isArrowImage =
    clickedElement.tagName === "IMG" &&
    clickedElement.alt === "right_arrow_small";
  const arrowContainer = clickedElement.closest(".size-4.transition-transform");
  const isArrowClick =
    isArrowImage || (arrowContainer && arrowContainer.contains(clickedElement));

  if (isArrowClick) {
    // Only toggle if clicking the arrow
    e.preventDefault();
    e.stopPropagation();
    toggleSubSection(sectionId, scrollCallback);
    return;
  }

  // For clicking anywhere else on the item: both toggle and navigate
  toggleSubSection(sectionId, scrollCallback);
  navigate(e);
};
</script>
